<template>
  <div class="archives">
    <HeroSingle :content="content.hero"></HeroSingle>

    <section ref="rootElRef" class="archives__grid">
      <div class="archives__grid-inner">
        <div v-if="isLoading" class="archives__grid__loading">
          <span class="spinner"></span>
        </div>

        <Spinner v-if="isLoading"></Spinner>

        <router-view v-slot="{ Component }">
          <Transition
            mode="out-in"
            @enter="onEnter"
            @leave="onLeave"
            @after-leave="onPageChange"
            @on-before-route-update="onPageChange"
          >
            <component
              :is="Component"
              :key="route.fullPath"
              :content="content"
            ></component>
          </Transition>
        </router-view>
      </div>
    </section>

    <BigCta v-if="content.bigCta" :content="content.bigCta"></BigCta>
  </div>
</template>

<script setup lang="ts">
import gsap from 'gsap'
import { ref } from 'vue'
import { useRoute } from 'vue-router'

import BigCta from '@/components/global/BigCta.vue'
import Spinner from '@/components/global/Spinner.vue'
import HeroSingle from '@/components/hero/Single.vue'
import { updateResource } from '@/core/resource'
import { getHeaderHeight, prefersReducedMotion } from '@/utils/motion'

import type { ResourceSingle } from '@/stores/resource'

const route = useRoute()

defineProps({
  content: {
    type: Object,
    required: true,
  },
})

// Refs
const rootElRef = ref<HTMLElement | null>(null)

const isLoading = ref(false)

const onEnter = (el: Element, done: () => void) => {
  const items = el.querySelectorAll('.card')

  gsap.fromTo(
    items,
    { opacity: 0, y: 20 },
    {
      opacity: 1,
      y: 0,
      stagger: 0.15,
      duration: 0.5,
      delay: 0.1,
      onComplete: () => done(),
    }
  )

  isLoading.value = false
}

const onLeave = (el: Element, done: () => void) => {
  const items = el.querySelectorAll('.card')

  gsap.fromTo(
    items,
    { opacity: 1, y: 0 },
    {
      opacity: 0,
      y: 20,
      duration: 0.3,
      onComplete: () => done(),
    }
  )

  isLoading.value = true
}

const onPageChange = () => {
  // Update resource, see App.vue
  if (route.meta.state) {
    updateResource(route.meta.state.resource as ResourceSingle)

    isLoading.value = false

    // Scroll to content
    if (rootElRef.value) {
      const yPosition =
        rootElRef.value.getBoundingClientRect().top +
        window.scrollY -
        getHeaderHeight()

      window.scrollTo({
        top: yPosition,
        behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.archives {
  // prettier-ignore
  @include fluid(padding-top, (xxs: 6rem, xxl: 12rem));

  :deep(.hero-single__title) {
    @include fluid(
      font-size,
      (
        m: 4rem,
        xxl: 10rem,
      )
    );
    @include fluid(
      line-height,
      (
        m: 4.4rem,
        xxl: 10rem,
      )
    );
  }
}

.archives__grid-inner {
  position: relative;
}
</style>
